import { BACKGROUND_JOB_STATES, BACKGROUND_QUEUES, type BackgroundJobState, type BackgroundQueue, type BackgroundQueueSummary } from '@nest-recruit/shared';
import type { Tone } from '../../../../components/Tone';

// ADMIN Background work: what is in the address (06-qol §3), so a copied link opens the same queue, list and job.

export type BackgroundTab = 'queues' | 'scheduled';

export interface BackgroundWorkSearch {
  readonly tab?: BackgroundTab;
  readonly queue?: BackgroundQueue;
  readonly state?: BackgroundJobState;
  readonly page?: number;
  readonly job?: string;
}

const JOB_ID = /^[\w:.-]{1,200}$/;

function oneOf<T extends string>(value: unknown, allowed: readonly T[]): T | undefined {
  return allowed.find((candidate) => candidate === value);
}

/** Anything odd in the address is dropped: the page opens on what it understood. */
export function readBackgroundWorkSearch(raw: Readonly<Record<string, unknown>>): BackgroundWorkSearch {
  const page = typeof raw.page === 'number' ? raw.page : Number(raw.page);
  const tab = oneOf(raw.tab, ['queues', 'scheduled'] as const);
  const queue = oneOf(raw.queue, BACKGROUND_QUEUES);
  const state = oneOf(raw.state, BACKGROUND_JOB_STATES);
  const job = typeof raw.job === 'string' ? raw.job : typeof raw.job === 'number' ? String(raw.job) : undefined;
  return {
    ...(tab ? { tab } : {}),
    ...(queue ? { queue } : {}),
    ...(state ? { state } : {}),
    ...(Number.isInteger(page) && page > 1 ? { page } : {}),
    ...(job !== undefined && JOB_ID.test(job) ? { job } : {}),
  };
}

/** The queue to show when none is chosen: the first with failures, else the first. */
export function defaultQueueOf(queues: readonly BackgroundQueueSummary[] | undefined): BackgroundQueue {
  return queues?.find((summary) => (summary.counts?.failed ?? 0) > 0)?.queue ?? BACKGROUND_QUEUES[0];
}

/** Failed jobs first (owner Q3): the failed list when there are any, else what is waiting. */
export function defaultStateOf(summary: BackgroundQueueSummary | undefined): BackgroundJobState {
  return (summary?.counts?.failed ?? 0) > 0 || summary?.counts === undefined ? 'failed' : 'waiting';
}

export const JOB_STATE_TONES: Readonly<Record<BackgroundJobState, Tone>> = {
  failed: 'urgent',
  waiting: 'new',
  active: 'progress',
  delayed: 'hold',
  completed: 'done',
};

/** A queue's health: red with failures, amber while Redis is away, green otherwise. */
export function healthOf(summary: BackgroundQueueSummary): 'bad' | 'unknown' | 'good' {
  if (summary.counts === null) return 'unknown';
  return summary.counts.failed > 0 ? 'bad' : 'good';
}
